interface AuthResponse {
  id_token?: string;
  token_type?: string;
}

interface JwtPayload {
  name?: string;
  email?: string;
  typ?: string;
  ent?: string[];
  exp?: number;
}

class AuthService {
  private _idToken: string | null = null;
  private _displayName: string | null = null;
  private _jwtPayload: JwtPayload | null = null;

  async ensureFreshToken(): Promise<void> {
    const exp = this._jwtPayload?.exp;
    if (!exp) return;
    const secsUntilExpiry = exp - Math.floor(Date.now() / 1000);
    if (secsUntilExpiry < 60) await this.tryRefresh();
  }

  async tryRefresh(): Promise<void> {
    if (!document.cookie.split(';').some(c => c.trim().startsWith('has_session='))) return;
    try {
      const res = await fetch('/api/ac/v1/token/refresh', {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) return;
      const data: AuthResponse = await res.json();
      if (!data.id_token) return;
      this._setToken(data.id_token);
    } catch {
      // no refresh token cookie or network error — user stays unauthenticated
    }
  }

  async loginWithGoogle(credential: string): Promise<void> {
    const response = await fetch('/api/ac/v1/token/authenticate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ googleToken: credential }),
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error(`Authentication failed: ${response.status}`);
    }
    const data: AuthResponse = await response.json();
    if (!data.id_token) throw new Error('No token in response');
    this._setToken(data.id_token);
  }

  private _setToken(token: string): void {
    this._idToken = token;
    this._jwtPayload = this._decodeJwt(token);
    this._displayName = this._jwtPayload?.name ?? this._jwtPayload?.email ?? '';
  }

  private _decodeJwt(token: string): JwtPayload | null {
    try {
      return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    try {
      await fetch('/api/ac/v1/token/logout', { method: 'GET', credentials: 'include' });
    } catch {
      // best-effort — clear local state regardless
    }
    this._idToken = null;
    this._displayName = null;
    this._jwtPayload = null;
  }

  isAuthenticated(): boolean {
    return this._idToken !== null;
  }

  getToken(): string | null {
    return this._idToken;
  }

  getDisplayName(): string {
    return this._displayName ?? '';
  }

  getUserType(): string {
    return this._jwtPayload?.typ ?? '';
  }

  getEntitlements(): string[] {
    return this._jwtPayload?.ent ?? [];
  }

  hasEntitlement(entitlement: string): boolean {
    return this.getEntitlements().includes(entitlement);
  }
}

export const authService = new AuthService();